/**
 * Error values that are safe to show in the UI.
 *
 * Firebase and browser errors are useful to developers but often contain
 * implementation details or provider wording that is not helpful to users.
 * Keep the original error available as `cause` at the call site and use this
 * module whenever an error crosses into user-facing state.
 */

export const UNKNOWN_ERROR_CODE = 'app/unknown'

export interface UserFacingErrorInfo {
  message: string
  code: string
}

export class UserFacingError extends Error {
  readonly code: string
  readonly userMessage: string

  constructor(message: string, code: string) {
    super(formatMessageWithCode(message, code))
    this.name = 'UserFacingError'
    this.userMessage = message
    this.code = code
  }
}

const FRIENDLY_MESSAGES: Record<string, string> = {
  'auth/invalid-credential':
    'Your email or password was not recognized. Check your details and try again.',
  'auth/invalid-email': 'Please enter a valid email address and try again.',
  'auth/user-disabled': 'This account is currently disabled. Please contact us for help.',
  'auth/too-many-requests':
    'There have been too many attempts. Please wait a moment and try again.',
  'auth/network-request-failed':
    'We could not reach the sign-in service. Check your connection and try again.',
  'functions/unauthenticated': 'Please sign in and try again.',
  'functions/permission-denied': 'You do not have permission to do that.',
  'functions/invalid-argument':
    'Some information is missing or invalid. Check your entries and try again.',
  'functions/not-found':
    'We could not find what you requested. It may have been removed or is no longer available.',
  'functions/failed-precondition':
    'This action cannot be completed right now. Refresh the page and try again.',
  'functions/deadline-exceeded':
    'This link or request has expired. Please use the latest link or try again.',
  'functions/resource-exhausted':
    'This action has reached its limit. Please wait a moment and try again.',
  'functions/unavailable': 'This service is temporarily unavailable. Please try again in a moment.',
  'functions/internal': 'Something went wrong on our end. Please try again in a moment.',
  'functions/unknown': 'Something went wrong while completing that request. Please try again.',
  'functions/cancelled': 'That request was cancelled. Please try again.',
  'permission-denied': 'You do not have permission to do that.',
  unauthenticated: 'Please sign in and try again.',
  'invalid-argument': 'Some information is missing or invalid. Check your entries and try again.',
  'not-found':
    'We could not find what you requested. It may have been removed or is no longer available.',
  'failed-precondition':
    'This action cannot be completed right now. Refresh the page and try again.',
  'deadline-exceeded': 'This link or request has expired. Please use the latest link or try again.',
  'resource-exhausted': 'This action has reached its limit. Please wait a moment and try again.',
  unavailable: 'This service is temporarily unavailable. Please try again in a moment.',
  internal: 'Something went wrong on our end. Please try again in a moment.',
  cancelled: 'That request was cancelled. Please try again.',
  'firestore/permission-denied': 'You do not have permission to access this information.',
  'firestore/unavailable':
    'We could not load this information. Check your connection and try again.',
  'storage/unauthorized': 'You do not have permission to upload or change this file.',
  'storage/canceled': 'The file upload was cancelled. Please try again.',
  'storage/retry-limit-exceeded':
    'The file upload took too long. Check your connection and try again.',
  'storage/unknown': 'We could not complete the file upload. Please try again.',
  'network-request-failed':
    'We could not connect to the service. Check your connection and try again.',
}

interface ErrorLike {
  code?: unknown
  message?: unknown
  details?: unknown
}

function asErrorLike(error: unknown): ErrorLike | null {
  return error !== null && typeof error === 'object' ? (error as ErrorLike) : null
}

function isSafeCode(value: string): boolean {
  return /^[a-z0-9][a-z0-9._/-]*$/i.test(value)
}

function readCode(error: unknown): string | null {
  const candidate = asErrorLike(error)
  const code = candidate?.code
  if (typeof code === 'string' && code.trim() && isSafeCode(code.trim())) {
    return code.trim()
  }

  const details = candidate?.details
  if (details && typeof details === 'object') {
    const detailCode = (details as { errorCode?: unknown }).errorCode
    if (typeof detailCode === 'string' && detailCode.trim() && isSafeCode(detailCode.trim())) {
      return detailCode.trim()
    }
  }

  return null
}

function fallbackCode(code: string): string {
  return isSafeCode(code) ? code : UNKNOWN_ERROR_CODE
}

function friendlyFallback(fallback: string): string {
  const message = fallback.trim() || 'Something went wrong. Please try again.'
  if (/^failed to /i.test(message)) {
    return `We couldn't ${message.slice('Failed to '.length).replace(/[.!?]+$/, '')}. Please try again.`
  }
  if (/^unable to /i.test(message)) {
    return `We couldn't ${message.slice('Unable to '.length).replace(/[.!?]+$/, '')}. Please try again.`
  }
  if (/^(.+?) failed[.!?]?$/i.test(message)) {
    const action = message
      .replace(/^(.+?) failed[.!?]?$/i, '$1')
      .trim()
      .toLowerCase()
    return `We couldn't complete ${action || 'that action'}. Please try again.`
  }
  return message
}

function messageForCode(code: string): string | undefined {
  return FRIENDLY_MESSAGES[code] ?? FRIENDLY_MESSAGES[code.replace(/^[^/]+\//, '')]
}

function formatMessageWithCode(message: string, code: string): string {
  return `${message.replace(/[.!?]+$/, '')}. (Error code: ${code})`
}

/** Return the provider/application code attached to an error. */
export function getErrorCode(error: unknown, code = UNKNOWN_ERROR_CODE): string {
  return readCode(error) ?? fallbackCode(code)
}

/** Convert an unknown error into safe, friendly UI copy and a support code. */
export function toUserFacingError(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
  code = UNKNOWN_ERROR_CODE
): UserFacingError {
  if (error instanceof UserFacingError) return error

  const errorCode = getErrorCode(error, code)
  const message = messageForCode(errorCode) ?? friendlyFallback(fallback)
  return new UserFacingError(message, errorCode)
}

/** Format an error for a UI string while retaining a stable support code. */
export function formatUserFacingError(
  error: unknown,
  fallback = 'Something went wrong. Please try again.',
  code = UNKNOWN_ERROR_CODE
): string {
  if (typeof error === 'string' && /\(Error code: [^)]+\)\.?$/.test(error.trim())) {
    return error
  }
  return toUserFacingError(error, fallback, code).message
}

/**
 * Format an application submission/upload error. Only validation copy explicitly
 * marked public by our callable is trusted; arbitrary provider details stay hidden.
 */
export function formatApplicationSubmissionError(
  error: unknown,
  fallback = 'We could not submit your application. Please check your entries and try again.'
): string {
  const candidate = asErrorLike(error)
  const callableCode = typeof candidate?.code === 'string' ? candidate.code : ''
  const details = candidate?.details
  if (
    (callableCode === 'functions/invalid-argument' ||
      callableCode === 'functions/failed-precondition' ||
      callableCode === 'invalid-argument' ||
      callableCode === 'failed-precondition') &&
    details !== null &&
    typeof details === 'object'
  ) {
    const validationDetails = details as { errorCode?: unknown; publicMessage?: unknown }
    if (
      validationDetails.errorCode === 'applications/validation' &&
      typeof validationDetails.publicMessage === 'string' &&
      validationDetails.publicMessage.trim().length > 0 &&
      validationDetails.publicMessage.length <= 500
    ) {
      return withErrorCode(validationDetails.publicMessage.trim(), 'applications/validation')
    }
  }

  return formatUserFacingError(error, fallback, 'applications/submission')
}

/** Add a support code to a message created locally by the UI. */
export function withErrorCode(message: string, code: string): string {
  if (/\(Error code: [^)]+\)\.?$/.test(message.trim())) return message
  return formatMessageWithCode(message, fallbackCode(code))
}

/** Create a typed, already-safe error for a validation or domain rule. */
export function createUserFacingError(message: string, code = 'app/validation'): UserFacingError {
  return new UserFacingError(message, fallbackCode(code))
}
